import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  FiMap,
  FiCreditCard,
  FiPieChart,
  FiBarChart2,
  FiActivity,
  FiClock,
  FiLayers,
  FiPlus,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import theme from '../UserDashboard/theme';
import { getClientEmail } from '../utils/getClientEmail';
import DashboardCardGrid, { dashboardGridStyles } from '../components/DashboardCardGrid';
import axios from '../utils/api.js';
import { getClientSubscription, fetchRobotHistory } from '../services/userRobotApi';
import { UserMapExplorer } from './RobotMap';
import { PaymentPlanView } from './paymentplan';
import { getPaymentStatusLabel, paymentStatusChipColors } from '../utils/paymentRefundDisplay';
import { PlanUsageView } from './Planusage';
import { RobotHistoryView } from './Robothistory';
import { AddonPlanView } from './Addonplan';

const ROBOT_SECTION_CARDS = [
  {
    id: 'map',
    title: 'Map',
    description: 'Rooms on floor plan',
    icon: <FiMap size={28} />,
    accent: '#0ea5e9',
  },
  {
    id: 'payment',
    title: 'Payment history',
    description: 'Account billing',
    icon: <FiCreditCard size={28} />,
    accent: '#8b5cf6',
  },
  {
    id: 'plan',
    title: 'Plan usage',
    description: 'Subscription hours',
    icon: <FiPieChart size={28} />,
    accent: '#06b6d4',
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'UVC hours by month',
    icon: <FiBarChart2 size={28} />,
    accent: '#0d9488',
  },
  {
    id: 'activity',
    title: 'Recent activity',
    description: 'Latest sessions',
    icon: <FiActivity size={28} />,
    accent: '#6366f1',
  },
  {
    id: 'history',
    title: 'History',
    description: 'All sessions',
    icon: <FiClock size={28} />,
    accent: '#ec4899',
  },
  {
    id: 'paymentplan',
    title: 'Payment Plan',
    description: 'Plans and billing for this robot',
    icon: <FiLayers size={28} />,
    accent: '#f59e0b',
  },
  {
    id: 'addons',
    title: 'Addons',
    description: 'Extra plans and add-ons',
    icon: <FiPlus size={28} />,
    accent: '#14b8a6',
  },
];

function getEmail() {
  return getClientEmail();
}

function formatINR(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(n));
}

function formatDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function monthLabel(d) {
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function historyToChart(historyRows) {
  const buckets = {};
  for (const h of historyRows) {
    const raw = h.disinfectionStartTime || h.date || h.createdAt;
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    const key = monthLabel(d);
    if (!buckets[key]) buckets[key] = { label: key, hours: 0, sessions: 0 };
    buckets[key].hours += (Number(h.disinfectionTimeTakenSeconds) || 0) / 3600;
    buckets[key].sessions += 1;
  }
  return Object.values(buckets).map((v) => ({
    ...v,
    hours: Math.round(v.hours * 10) / 10,
  }));
}

function historyToRecentActivity(historyRows, limit = 24) {
  return [...historyRows]
    .sort((a, b) => {
      const ta = new Date(a.disinfectionStartTime || a.date || 0).getTime();
      const tb = new Date(b.disinfectionStartTime || b.date || 0).getTime();
      return tb - ta;
    })
    .slice(0, limit)
    .map((h, i) => {
      const start = h.disinfectionStartTime ? new Date(h.disinfectionStartTime) : null;
      return {
        id: h._id || i,
        roomName:
          h.roomName != null && String(h.roomName).trim()
            ? String(h.roomName).trim()
            : '—',
        date: start
          ? start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          : '—',
        time: start
          ? start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
          : '—',
        duration: h.disinfectionTimeTakenSeconds
          ? `${Math.round(h.disinfectionTimeTakenSeconds / 60)} min`
          : '—',
        method: h.mode === 'auto' || h.mode === 'Auto' ? 'Auto' : 'Manual',
      };
    });
}

function BackButton({ onClick, label }) {
  return (
    <IconButton onClick={onClick} size="medium" sx={backBtnSx} aria-label={label}>
      <ArrowBackIcon />
    </IconButton>
  );
}

export default function RobotDetailView({ robot, onBack }) {
  const [activeSection, setActiveSection] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [planName, setPlanName] = useState(null);
  const [loadingPay, setLoadingPay] = useState(true);
  const [loadingHist, setLoadingHist] = useState(true);
  const [errPay, setErrPay] = useState('');
  const [errHist, setErrHist] = useState('');

  const email = getEmail();
  const robotId = robot?.robotId;

  const loadPaymentHistory = useCallback(async () => {
    try {
      const res = await axios.get('/user/payment-history');
      const rows = res.data?.data;
      if (Array.isArray(rows)) {
        setPaymentHistory(
          rows.map((p) => ({
            ...p,
            paymentDate: p.createdAt || p.paymentDate,
          })),
        );
        setErrPay('');
        return;
      }
      setPaymentHistory([]);
    } catch (payErr) {
      if (email) {
        try {
          const subRes = await getClientSubscription(email);
          const usd = subRes.data?.userSubscriptionDetails || subRes.data || {};
          setPaymentHistory(Array.isArray(usd.paymentHistory) ? usd.paymentHistory : []);
          setErrPay('');
          return;
        } catch {
          // fall through
        }
      }
      setPaymentHistory([]);
      setErrPay(
        payErr.response?.data?.message || 'Could not load payment history.',
      );
    }
  }, [email]);

  const loadDetail = useCallback(async () => {
    if (!robotId || !email) {
      const msg = 'Email not found. Please sign in again.';
      setErrPay(msg);
      setErrHist(msg);
      setLoadingPay(false);
      setLoadingHist(false);
      return;
    }

    setLoadingPay(true);
    setLoadingHist(true);
    setErrPay('');
    setErrHist('');

    const [histRes] = await Promise.all([
      fetchRobotHistory(robotId).catch((e) => ({ error: e })),
      loadPaymentHistory(),
    ]);

    setLoadingPay(false);

    if (histRes.error) {
      setHistoryRows([]);
      setErrHist(histRes.error.response?.data?.message || 'Could not load activity.');
    } else {
      setHistoryRows(Array.isArray(histRes.data?.data) ? histRes.data.data : []);
    }
    setLoadingHist(false);
  }, [email, robotId, loadPaymentHistory]);

  const handlePaymentSuccess = useCallback(() => {
    loadPaymentHistory();
  }, [loadPaymentHistory]);

  useEffect(() => {
    setActiveSection(null);
    setPlanName(null);
    loadDetail();
  }, [loadDetail, robotId]);

  const chartData = useMemo(() => historyToChart(historyRows), [historyRows]);
  const recentActivity = useMemo(() => historyToRecentActivity(historyRows), [historyRows]);
  const locationLine = [robot.location, robot.subLocation].filter(Boolean).join(' · ');

  const totalSessions = historyRows.length;
  const totalHours = useMemo(
    () =>
      Math.round(
        (historyRows.reduce((s, h) => s + (Number(h.disinfectionTimeTakenSeconds) || 0), 0) / 3600) *
          10,
      ) / 10,
    [historyRows],
  );

  const activeMeta = ROBOT_SECTION_CARDS.find((s) => s.id === activeSection);

  const sectionLoading =
    (activeSection === 'payment' && loadingPay) ||
    ((activeSection === 'analytics' || activeSection === 'activity') && loadingHist);

  const sectionError =
    (activeSection === 'payment' && errPay) ||
    (activeSection === 'analytics' && errHist) ||
    (activeSection === 'activity' && errHist) ||
    '';

  const handleBack = () => {
    if (activeSection) setActiveSection(null);
    else onBack();
  };

  const robotPageSubtitle = `${robot.robotId} · ${robot.model || '—'}${locationLine ? ` · ${locationLine}` : ''}`;

  const sectionSubtitle = () => {
    if (activeSection === 'payment') return 'Account billing (shared across robots)';
    if (activeSection === 'analytics') return `Last 90 days · ${totalSessions} sessions · ${totalHours}h UVC`;
    if (activeSection === 'map') return "Rooms on this robot's floor plan";
    if (activeSection === 'plan' && planName) return `Plan: ${planName}`;
    if (activeSection === 'activity') return 'Latest disinfection sessions for this robot';
    if (activeSection === 'history') return 'All disinfection sessions for this robot';
    if (activeSection === 'paymentplan') return 'Payment Plan for this robot';
    if (activeSection === 'addons') return 'Addons for this robot';
    return activeMeta?.description;
  };

  const renderSectionBody = () => {
    switch (activeSection) {
      case 'map':
        return <UserMapExplorer robotId={robotId} compact />;

      case 'payment':
        if (paymentHistory.length === 0) {
          return (
            <Typography sx={{ fontSize: 14, color: '#64748b' }}>
              No payments recorded yet.
            </Typography>
          );
        }
        return (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Date</TableCell>
                  <TableCell sx={thSx}>Plan</TableCell>
                  <TableCell sx={thSx} align="right">
                    Amount
                  </TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((p, i) => (
                  <TableRow key={p.razorpay_payment_id || p.razorpay_order_id || i}>
                    <TableCell sx={tdSx}>{formatDateShort(p.paymentDate)}</TableCell>
                    <TableCell sx={tdSx}>{p.planName || p.type || '—'}</TableCell>
                    <TableCell sx={tdSx} align="right">
                      {formatINR(p.finalPrice ?? p.amount)}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      {(() => {
                        const colors = paymentStatusChipColors(p);
                        return (
                          <Chip
                            label={getPaymentStatusLabel(p)}
                            size="small"
                            sx={{
                              fontSize: 11,
                              height: 22,
                              fontWeight: 600,
                              bgcolor: colors.bg,
                              color: colors.color,
                            }}
                          />
                        );
                      })()}
                      {p.refund?.refundedAt ? (
                        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
                          {formatDateShort(p.refund.refundedAt)}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'plan':
        return <PlanUsageView robotId={robotId} onPlanNameChange={setPlanName} />;

      case 'analytics':
        if (chartData.length === 0) {
          return (
            <Typography sx={{ fontSize: 14, color: '#64748b' }}>
              No session data to chart yet.
            </Typography>
          );
        }
        return (
          <Box sx={{ width: '100%', height: { xs: 280, md: 360 } }}>
            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2 }}>
              Last 90 days · {totalSessions} sessions · {totalHours}h UVC
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickFormatter={(v) => `${v}h`}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid rgba(15, 23, 42, 0.08)' }}
                />
                <Bar dataKey="hours" fill={theme.colors.brandPrimary} radius={[6, 6, 0, 0]} name="UVC hours" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'activity':
        if (recentActivity.length === 0) {
          return (
            <Typography sx={{ fontSize: 14, color: '#64748b' }}>
              No recent sessions.
            </Typography>
          );
        }
        return (
          <Grid container spacing={2} sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
            {recentActivity.map((s) => (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                    {s.roomName}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
                    {s.date} · {s.time}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Chip label={s.duration} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>{s.method}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 'paymentplan':
        return (
          <PaymentPlanView
            compact
            robotId={robotId}
            onPaymentSuccess={handlePaymentSuccess}
          />
        );

      case 'history':
        return <RobotHistoryView robotId={robotId} />;

      case 'addons':
        return (
          <AddonPlanView
            compact
            robotId={robotId}
            onPaymentSuccess={handlePaymentSuccess}
          />
        );

      default:
        return null;
    }
  };

  const embeddedSections = [
    'map',
    'plan',
    'paymentplan',
    'history',
    'addons',
  ].includes(activeSection);

  if (!activeSection) {
    return (
      <DashboardCardGrid
        embedded
        filterByRole={false}
        pageTitle={robot.username || robot.robotId}
        pageSubtitle={robotPageSubtitle}
        cardData={ROBOT_SECTION_CARDS}
        onCardClick={(card) => setActiveSection(card.id)}
        headerLeading={<BackButton onClick={handleBack} label="Back to robots" />}
      />
    );
  }

  return (
    <Box style={dashboardGridStyles.embeddedWrap}>
      <Box style={dashboardGridStyles.embeddedHeaderSection}>
        <Box style={dashboardGridStyles.headerRow}>
          <Box style={dashboardGridStyles.headerLeading}>
            <BackButton onClick={handleBack} label="Back to sections" />
          </Box>
          <Box style={dashboardGridStyles.headerTextBlock}>
            <Typography component="h1" style={dashboardGridStyles.pageTitle}>
              {activeMeta?.title}
            </Typography>
            <Typography style={dashboardGridStyles.pageSubtitle}>{sectionSubtitle()}</Typography>
          </Box>
        </Box>
      </Box>

      <Box style={dashboardGridStyles.embeddedMainContent}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: '16px',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            bgcolor: '#ffffff',
            minHeight: 320,
          }}
        >
          {embeddedSections ? (
            renderSectionBody()
          ) : sectionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
              <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
            </Box>
          ) : sectionError ? (
            <Typography sx={{ fontSize: 14, color: theme.colors.accentDanger }}>{sectionError}</Typography>
          ) : (
            renderSectionBody()
          )}
        </Card>
      </Box>
    </Box>
  );
}

const backBtnSx = {
  bgcolor: 'white',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  '&:hover': { bgcolor: '#f8fafc' },
};
const thSx = { fontWeight: 700, fontSize: 11, color: '#64748b', py: 1 };
const tdSx = { fontSize: 13, py: 1.25 };
